/*eslint eqeqeq: "off"*/



/*******************************************************************************
* Class        : UTM                       ( Universal Transversor Mercator )
* Date         : 11/09/2003
* Author       : Adriano, Hermes David Jr
* Source       : WebTrace
* 
* Construtor   : UTM(options)
*
*                  options {
*                     datum            string       Nomes de datum pre-configurados, podendo ser "WGS84", "SAD69" ou "SIRGAS2000" para evitar informar os valores de "a" e "b"
*                     zone:            int          Fuso UTM, valor de 1 a 60
*                     south:           boolean      Indica se o hemisferio e Sul ou nao
*                     a                number       Raio Equatorial do datum. Opcional se o datum for informado.
*                     b                number       Raio Polar do datum. Opcional se o datum for informado.
*                  }
* 
* Definitions
*                ._utm_sm_a           number        Raio Equatorial
*                ._utm_sm_b           number        Raio Polar
*                ._utm_ep2            number        Excentricidade
*                ._utm_Flattening     number        Flaterning
*                ._utm_Excen2         number        Excentricidade ao quadrado
*                ._utm_n              number        Nort
*                ._utm_alpha          number        Alfa
*                ._utm_beta           number        Beta
*                ._utm_gamma          number        Gamma
*                ._utm_delta          number        Delta
*                ._utm_epsilon        number        Epsilon
*
*                ._utm_zone           number        Fuso UTM, valor de 1 a 60
*                ._utm_south          boolean       Indica se o hemisferio e Sul ou nao
*                ._utm_Lambda0        number        Logitude do Meridiano Central em radianos
* 
* Static Methods 
*                .getUTMZone                        Calcula a zona UTM a partir da Longitude
*                .isSouthHemisphere                 Identifica se o Hemisferio e sul a partir da Latitude
* 
* Instance Methods
*                .toProjected                       Transforma a coordenada geografica (longitude e latitude em graus) para a projecao UTM (easting e northing)
*                .toGeografic                       Transforma a coordenada projetada UTM (easting e northing) para geografico (longitude e latitude em graus)
* 
*******************************************************************************/

const RADIAN_FACTOR = Math.PI/180; // radians per degree factor
const _k0  = 0.9996; // Scale Factor

class UTM {

  //------------------------------------------------------------------------------
  //  Construtor
  //------------------------------------------------------------------------------
  
  constructor(options) {

    //privates
    this._utm_sm_a = null;
    this._utm_sm_b = null;
    this._utm_ep2 = null;
    this._utm_Flattening = null;
    this._utm_Excen2 = null;
    this._utm_n = null;
    this._utm_alpha = null;
    this._utm_beta = null;
    this._utm_gamma = null;
    this._utm_delta = null;
    this._utm_epsilon = null;

    this._utm_zone = null;
    this._utm_south = null;
    this._utm_Lambda0 = null;


    //initialize
    options = options || { datum: "WGS84", zone: 21, south: true };

    if (options.datum) {
      switch (options.datum) {
        case "SAD69":
          this._utm_sm_a = 6378160;
          this._utm_sm_b = 6356774.71919531;
          break;
        case "WGS84":
          this._utm_sm_a = 6378137;
          this._utm_sm_b = 6356752.31424518;
          break;
        case "SIRGAS2000":
          this._utm_sm_a = 6378137;
          this._utm_sm_b = 6356752.3141403561;
          break;
        default:
          throw new Error("UTM: \"" + options.datum + "\" is not a valid ellipsoid!");
      }
    }
    else {
      this._utm_sm_a = options.a;
      this._utm_sm_b = options.b;
    }

    this._utm_zone = options.zone || 21;
    this._utm_south = options.south == null ? true : options.south;
    


    // Inicializa as configurações UTM
    this._utm_ep2         = (Math.pow(this._utm_sm_a, 2) - Math.pow(this._utm_sm_b, 2)) / Math.pow(this._utm_sm_b, 2);
    this._utm_Flattening  = (this._utm_sm_a - this._utm_sm_b) / (this._utm_sm_a);
    this._utm_Excen2      = (this._utm_Flattening * 2) - Math.pow(this._utm_Flattening, 2);
    this._utm_n           = (this._utm_sm_a - this._utm_sm_b) / (this._utm_sm_a + this._utm_sm_b);
    this._utm_alpha       = ( (this._utm_sm_a + this._utm_sm_b) / 2) * (1 + Math.pow(this._utm_n,2) / 4) + (Math.pow(this._utm_n,4) / 64 );
    this._utm_beta        = (3 * this._utm_n / 2) + (-27 * Math.pow(this._utm_n,3) / 32) + (269 * Math.pow(this._utm_n,5) / 512);
    this._utm_gamma       = (21 * Math.pow(this._utm_n,2) / 16) + (-55 * Math.pow(this._utm_n,4) / 32);
    this._utm_delta       = (151 * Math.pow(this._utm_n,3) /96) + (-417 * Math.pow(this._utm_n,5) / 128);
    this._utm_epsilon     = (1097 * Math.pow(this._utm_n,4) / 512);

    // Coordenada do Meridiano Central em radianos
    this._utm_Lambda0 = (this._utm_zone * 6 - 183) * RADIAN_FACTOR;

  }



  //------------------------------------------------------------------------------
  //  Static Methods
  //------------------------------------------------------------------------------

  static getUTMZone(lon) {
    while (lon<=-180)
      lon += 360;

    while (lon>180)
      lon -= 360;

    return Math.ceil((lon + 180)/6);
  }

  static isSouthHemisphere(lat) {
    return lat<0;
  }  



  //------------------------------------------------------------------------------
  //  Private Methods
  //------------------------------------------------------------------------------

  _footPointLatitude ( p_y ) {
    var v_y  = p_y / this._utm_alpha;
    
    return ( v_y + (this._utm_beta * Math.sin(2 * v_y)) + (this._utm_gamma * Math.sin(4 * v_y)) + (this._utm_delta * Math.sin(6 * v_y)) + (this._utm_epsilon * Math.sin(8 * v_y)) );
  }


  _arcLengthOfMeridian( radianLat ) {
    var v_n2 = this._utm_n * this._utm_n;
    var v_n3 = v_n2 * this._utm_n;
    var v_n4 = v_n3 * this._utm_n;
    var v_a0 = 1.0 + v_n2 / 4 + v_n4 / 64;
    var v_a2 = 3.0 / 2 * (this._utm_n - v_n3 / 8);
    var v_a4 = 15.0 / 16 * (v_n2 - v_n4 / 4);
    var v_a6 = 35.0 / 48 * v_n3;
    var v_a8 = 315.0 / 512 * v_n4;

    return ( this._utm_sm_a / (1 + this._utm_n) * (v_a0 * radianLat - v_a2 * Math.sin(2 * radianLat) + v_a4 * Math.sin(4 * radianLat) - v_a6 * Math.sin(6 * radianLat) + v_a8 * Math.sin(8 * radianLat)) );
  }



  //------------------------------------------------------------------------------
  //  Public Methods
  //------------------------------------------------------------------------------

  toProjected( lon, lat ) {
    var radianLon = lon * RADIAN_FACTOR;
    var radianLat = lat * RADIAN_FACTOR;


    var v_cos = Math.cos(radianLat);
    var v_utm_nu2 = (this._utm_Excen2/(1 - this._utm_Excen2)*Math.pow(v_cos, 2));
    var v_utm_n = Math.pow(this._utm_sm_a, 2)/(this._utm_sm_b*Math.sqrt(1 + v_utm_nu2));
    var v_t = Math.tan(radianLat);
    var v_t2 = v_t*v_t;


    // Longitude (Delta)
    var v_DeltaLambda = radianLon - this._utm_Lambda0; 
    //v_DeltaLambda = Math.abs(this._utm_Lambda0) - Math.abs(p_longitude);

    //Coeficientes para o cálculo de Easting
    var v_l3coef = 1 - v_t2 + v_utm_nu2;
    var v_l5coef = 5 - 18*v_t2 + (v_t2*v_t2) + 14*v_utm_nu2 - 58*v_t2*v_utm_nu2;
    var v_l7coef = 61 - 479*v_t2 + 179*(v_t2*v_t2) - (v_t2*v_t2*v_t2);

    var v_l1coefT = v_utm_n*v_cos*v_DeltaLambda;

    var v_l3coefT = (v_utm_n/6*Math.pow(v_cos, 3)*v_l3coef*Math.pow(v_DeltaLambda, 3));
    var v_l5coefT = (v_utm_n/120*Math.pow(v_cos, 5)*v_l5coef*Math.pow(v_DeltaLambda, 5));
    var v_l7coefT = (v_utm_n/5040*Math.pow(v_cos, 7)*v_l7coef*Math.pow(v_DeltaLambda, 7));

    var v_xi = (v_l1coefT + v_l3coefT + v_l5coefT + v_l7coefT)*_k0;

    // Calculo de Easting
    var v_E = v_xi + 500000;

    //Coeficientes para cálculo do Northing
    var v_l4coef = 5 - v_t2 + 9*v_utm_nu2 + 4*(v_utm_nu2*v_utm_nu2);
    var v_l6coef = 61 - 58*v_t2 + (v_t2*v_t2) + 270*v_utm_nu2 - 330*v_t2*v_utm_nu2;
    var v_l8coef = 1385 - 3111*v_t2 + 543*(v_t2*v_t2) - (v_t2*v_t2*v_t2);

    // Calculo de northing
    var v_N = this._arcLengthOfMeridian(radianLat)
              + (v_t/2*v_utm_n*Math.pow(v_cos, 2)*Math.pow(v_DeltaLambda, 2))
              + (v_t/24*v_utm_n*Math.pow(v_cos, 4)*v_l4coef*Math.pow(v_DeltaLambda, 4))
              + (v_t/720*v_utm_n*Math.pow(v_cos, 6)*v_l6coef*Math.pow(v_DeltaLambda, 6))
              + (v_t/40320*v_utm_n*Math.pow(v_cos, 8)*v_l8coef*Math.pow(v_DeltaLambda, 8));


    v_N = v_N * _k0;

    if (this._utm_south)
      v_N += 10000000;

    return [v_E, v_N];
  }


  toGeographic( easting, northing){

    var v_E =  (easting-500000)/ _k0;
    
    var v_phif = this._footPointLatitude( ( ( this._utm_south && ( northing != 0 ) ) ? (northing - 10000000): northing ) / _k0 );
    
    var v_cf = Math.cos(v_phif);
    var v_nuf2 = this._utm_ep2 * Math.pow(v_cf,2);
    var v_Nf = Math.pow(this._utm_sm_a,2) / (this._utm_sm_b * Math.sqrt(1 + v_nuf2));
    var v_Nfpow = v_Nf;
    
    var v_tF = Math.tan(v_phif);
    var v_tf2 = v_tF * v_tF;		//	v_tF^2)
    var v_tf4 = v_tf2 * v_tf2;	 //	v_tf2^2)
    
    //  Calcular os coeficientes fracionais de Easting elevado a n que darão origem a latitude e longitude.
    var v_x1frac  = 1.0 / (v_Nfpow * v_cf);
    
    v_Nfpow = v_Nfpow * v_Nf;	//	v_Nf^2)
    var v_x2frac  = v_tF / (2 * v_Nfpow);
    
    v_Nfpow = v_Nfpow * v_Nf;	//	 v_Nf^3)
    var v_x3frac = 1.0 / (6 * v_Nfpow * v_cf);
    
    v_Nfpow = v_Nfpow * v_Nf;	//	 v_Nf^4)
    var v_x4frac  = v_tF / (24 * v_Nfpow);
    
    v_Nfpow = v_Nfpow * v_Nf;	//	 v_Nf^5)
    var v_x5frac = 1.0 / (120 * v_Nfpow * v_cf);
    
    v_Nfpow = v_Nfpow * v_Nf;	//	 v_Nf^6)
    var v_x6frac = v_tF / (720 * v_Nfpow);
    
    v_Nfpow = v_Nfpow * v_Nf;	//	 v_Nf^7)
    var v_x7frac = 1.0 / (5040 * v_Nfpow * v_cf);
    
    v_Nfpow = v_Nfpow * v_Nf;	//	 v_Nf^8)
    var v_x8frac = v_tF / (40320 * v_Nfpow);
    
    var v_x2poly = -1 - v_nuf2;
    
    var v_x3poly = -1 - 2 * v_tf2 - v_nuf2;
    
    var v_x4poly = 5 + 3 * v_tf2 + 6 * v_nuf2 - 6 * v_tf2 * v_nuf2 - 3 * (v_nuf2 * v_nuf2) - 9 * v_tf2 * (v_nuf2 * v_nuf2);
    
    var v_x5poly = 5 + 28 * v_tf2 + 24 * v_tf4 + 6 * v_nuf2 + 8 * v_tf2 * v_nuf2;
    
    var v_x6poly = -61 - 90 * v_tf2 - 45 * v_tf4 - 107 * v_nuf2 + 162 * v_tf2 * v_nuf2;
    
    var v_x7poly = -61 - 662 * v_tf2 - 1320 * v_tf4 - 720 * (v_tf4 * v_tf2);
    
    var v_x8poly = 1385 + 3633 * v_tf2 + 4095 * v_tf4 + 1575 * (v_tf4 * v_tf2);

    
    var lat = v_phif + v_x2frac * v_x2poly * (v_E * v_E) + v_x4frac * v_x4poly * Math.pow(v_E,4) + v_x6frac * v_x6poly * Math.pow(v_E,6) + v_x8frac * v_x8poly * Math.pow(v_E,8);
    var lon = this._utm_Lambda0 + v_x1frac * v_E + v_x3frac * v_x3poly * Math.pow(v_E,3) + v_x5frac * v_x5poly * Math.pow(v_E,5) + v_x7frac * v_x7poly * Math.pow(v_E,7);

    return [lon / RADIAN_FACTOR, lat / RADIAN_FACTOR];
  }


}

if (typeof global !== 'undefined') {
	global.UTM = UTM;
}